export function normalizePublicKeyBase64(publicKeyBase64) {
    if (!publicKeyBase64 || typeof publicKeyBase64 !== 'string') {
        throw new Error('Public key is missing.');
    }

    let cleanBase64 = publicKeyBase64.trim();

    if ((cleanBase64.startsWith('"') && cleanBase64.endsWith('"')) || (cleanBase64.startsWith("'") && cleanBase64.endsWith("'"))) {
        cleanBase64 = cleanBase64.slice(1, -1);
    }

    cleanBase64 = cleanBase64
        .replace(/\\n/g, '\n')
        .replace(/-----BEGIN PUBLIC KEY-----/g, '')
        .replace(/-----END PUBLIC KEY-----/g, '')
        .replace(/\s/g, '')
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    if (!cleanBase64) {
        throw new Error('Public key is missing.');
    }

    const paddingLength = cleanBase64.length % 4;
    if (paddingLength) {
        cleanBase64 += '='.repeat(4 - paddingLength);
    }

    return cleanBase64;
}

function base64ToArrayBuffer(base64) {
    const cleanBase64 = normalizePublicKeyBase64(base64);
    let binary = '';

    try {
        binary = window.atob(cleanBase64);
    } catch (error) {
        throw new Error('Public key is not valid base64.');
    }

    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes.buffer;
}

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';

    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return window.btoa(binary);
}

export function normalizeSSN(ssn) {
    return String(ssn || '').replace(/\D/g, '');
}

export function validateSSN(ssn) {
    const normalizedSSN = normalizeSSN(ssn);

    if (!/^\d{9}$/.test(normalizedSSN)) {
        throw new Error('Invalid SSN. Expected 9 digits.');
    }

    return normalizedSSN;
}

/**
 * Encrypt SSN in browser using RSA-OAEP SHA-256.
 *
 * publicKeyBase64 should be the Base64 body of the public key,
 * without:
 * -----BEGIN PUBLIC KEY-----
 * -----END PUBLIC KEY-----
 */
export async function encryptSSNWithPublicKeyBase64(ssn, publicKeyBase64) {
    const normalizedSSN = validateSSN(ssn);

    if (!window.crypto || !window.crypto.subtle) {
        throw new Error('Web Crypto API is not supported in this browser.');
    }

    const publicKey = await window.crypto.subtle.importKey(
        'spki',
        base64ToArrayBuffer(publicKeyBase64),
        {
            name: 'RSA-OAEP',
            hash: 'SHA-256',
        },
        false,
        ['encrypt']
    );

    const encryptedBuffer = await window.crypto.subtle.encrypt(
        {
            name: 'RSA-OAEP',
        },
        publicKey,
        new TextEncoder().encode(normalizedSSN)
    );

    return arrayBufferToBase64(encryptedBuffer);
}
